import { useState, useEffect } from "react";
import Modal from "../../components/common/Modal";
import CrudForm from "../../components/common/crud/CrudForm";
import customerService from "../../services/customerService";
import countryService from "../../services/countryService";
import alertService from "../../utils/alertService";
import { normalizeApiError } from "../../utils/apiError";
import { useMasterModals } from "../../hooks/useMasterModals";

const customerFormSchema = [
  {
    name: "customerCode",
    label: "Customer Code",
    type: "text",
    placeholder: "e.g. CUST001",
    validation: { required: true, pattern: /^[a-zA-Z0-9-_]+$/ },
  },
  {
    name: "customerName",
    label: "Customer Name",
    type: "text",
    placeholder: "e.g. John Doe",
  },
  {
    name: "companyName",
    label: "Company Name",
    type: "text",
    placeholder: "e.g. Acme Corp",
    validation: { required: true },
  },
  {
    name: "email",
    label: "Email",
    type: "text",
    placeholder: "john@example.com",
    validation: {
      pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      patternMessage: "Invalid email format",
    },
  },
  {
    name: "mobileNo",
    label: "Mobile No",
    type: "text",
    placeholder: "e.g. 1234567890",
  },
  {
    name: "address",
    label: "Address",
    type: "textarea",
    placeholder: "Enter address",
    validation: { required: true },
  },
  {
    name: "countryId",
    label: "Country",
    type: "dropdown",
    validation: { required: true },
  },
  {
    name: "isActive",
    label: "Is Active",
    type: "checkbox",
    defaultValue: true,
  },
];

function CustomerCreateModal({ isOpen, onClose, onSuccess }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [countryOptions, setCountryOptions] = useState([]);

  const loadCountries = async () => {
    try {
      const countries = await countryService.getActiveCountries();
      setCountryOptions(
        countries.map((c) => ({
          label: c.countryName || c.name,
          value: c.countryId || c.id,
        }))
      );
    } catch (err) {
      console.error("Failed to load countries", err);
    }
  };

  const { openModal, renderModals } = useMasterModals({
    country: loadCountries,
  });

  useEffect(() => {
    if (isOpen) {
      loadCountries();
    }
  }, [isOpen]);

  const handleSubmit = async (values) => {
    setIsSubmitting(true);
    try {
      await customerService.createCustomer(values);
      alertService.success("Customer created successfully.");
      onSuccess?.();
      onClose();
    } catch (err) {
      alertService.error(normalizeApiError(err).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add New Customer" size="lg">
      <CrudForm
        mode="create"
        schema={customerFormSchema.map((f) =>
          f.name === "countryId"
            ? {
                ...f,
                options: countryOptions,
                onAdd: () => openModal("country"),
                addLabel: "Add New Country",
              }
            : f
        )}
        initialValues={{ isActive: true }}
        isSubmitting={isSubmitting}
        onSubmit={handleSubmit}
        onCancel={onClose}
      />
      {renderModals()}
    </Modal>
  );
}

export default CustomerCreateModal;
